/**
 * SubscriptionEndDatePanel
 *
 * Displays subscription end date info in the admin user profile, including:
 * - effectiveEndDate with warning/expired indicators (Req 2.1, 2.5, 2.6, 2.7)
 * - baseEndDate + promoExtensionDays when promo days > 0 (Req 2.2)
 * - Lifetime and inactive states (Req 2.3, 2.4)
 * - Subscription event timeline (Req 8.1, 8.5)
 * - Manual promo extension adjustment form (Req 8.2, 8.3, 8.4)
 * - Family plan context (Req 11.5, 11.6)
 */

import { useState, useEffect, useCallback } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';
import { API_ENDPOINTS } from '../../config/api-endpoints';
import './SubscriptionEndDatePanel.css';

// ─── Helpers ────────────────────────────────────────────────────────────────

/** Format ISO date string as "DD MMM YYYY" in Asia/Singapore timezone */
const formatSGT = (isoStr) => {
  if (!isoStr) return 'N/A';
  return new Date(isoStr).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    timeZone: 'Asia/Singapore',
  });
};

/** Format ISO timestamp for event timeline */
const formatEventTime = (isoStr) => {
  if (!isoStr) return '';
  return new Date(isoStr).toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Asia/Singapore',
  });
};

const EVENT_LABELS = {
  subscription_renewed: '🔄 Subscription Renewed',
  promo_extension_applied: '🎁 Promo Extension Applied',
  promo_days_forfeited: '⚠️ Promo Days Forfeited',
  subscription_activated: '✅ Subscription Activated',
  manual_adjustment: '🔧 Manual Adjustment',
};

// ─── Sub-components ──────────────────────────────────────────────────────────

const EndDateDisplay = ({ info }) => {
  if (!info) return null;

  const { effectiveEndDate, baseEndDate, promoExtensionDays, isLifetime, isExpired, daysUntilExpiry } = info;

  if (isLifetime) {
    return (
      <div className="sed-panel__end-date">
        <div className="sed-panel__lifetime">♾️ Lifetime — No Expiry</div>
      </div>
    );
  }

  const isWarning = !isExpired && daysUntilExpiry !== null && daysUntilExpiry <= 30;

  return (
    <div className="sed-panel__end-date">
      <div className="sed-panel__info-row">
        <span className="sed-panel__label">Subscription Ends</span>
        <span className={`sed-panel__date ${isExpired ? 'sed-panel__date--expired' : isWarning ? 'sed-panel__date--warning' : ''}`}>
          {isExpired && <span className="sed-panel__indicator sed-panel__indicator--expired">● Expired</span>}
          {isWarning && <span className="sed-panel__indicator sed-panel__indicator--warning">● Expiring Soon</span>}
          {formatSGT(effectiveEndDate)}
          {daysUntilExpiry !== null && !isExpired && (
            <span className="sed-panel__days-remaining">({daysUntilExpiry}d remaining)</span>
          )}
        </span>
      </div>

      {promoExtensionDays > 0 && (
        <>
          <div className="sed-panel__info-row">
            <span className="sed-panel__label">Base End Date</span>
            <span className="sed-panel__value">{formatSGT(baseEndDate)}</span>
          </div>
          <div className="sed-panel__info-row">
            <span className="sed-panel__label">Promo Days Added</span>
            <span className="sed-panel__value sed-panel__promo-days">+{promoExtensionDays} days</span>
          </div>
        </>
      )}
    </div>
  );
};

const FamilyContext = ({ user, info }) => {
  const accountType = user?.attributes?.['custom:account_type'];
  const studentCount = user?.attributes?.['custom:student_count'];
  const familyId = user?.attributes?.['custom:family_id'];
  const parentAccountId = user?.attributes?.['custom:parent_account_id'];

  if (accountType === 'family_parent') {
    return (
      <div className="sed-panel__family-context">
        <span className="sed-panel__family-badge sed-panel__family-badge--parent">👨‍👩‍👧 Family Parent</span>
        {studentCount && (
          <span className="sed-panel__family-note">{studentCount} linked child account{studentCount !== '1' ? 's' : ''}</span>
        )}
      </div>
    );
  }

  if (accountType === 'child' || accountType === 'family_child') {
    return (
      <div className="sed-panel__family-context">
        <span className="sed-panel__family-badge sed-panel__family-badge--child">👦 Family Child</span>
        <div className="sed-panel__family-note">
          End date governed by parent account
          {parentAccountId && (
            <span className="sed-panel__mono"> ({parentAccountId.slice(0, 8)}...)</span>
          )}
        </div>
      </div>
    );
  }

  return null;
};

const EventTimeline = ({ events }) => {
  if (!events?.length) {
    return (
      <div className="sed-panel__empty">No subscription events recorded.</div>
    );
  }

  return (
    <div className="sed-panel__timeline">
      {events.map((evt, i) => (
        <div key={i} className="sed-panel__timeline-item">
          <div className="sed-panel__timeline-dot" />
          <div className="sed-panel__timeline-body">
            <div className="sed-panel__timeline-header">
              <span className="sed-panel__timeline-type">
                {EVENT_LABELS[evt.eventType] || evt.eventType}
              </span>
              <span className="sed-panel__timeline-time">{formatEventTime(evt.createdAt)}</span>
            </div>
            <div className="sed-panel__timeline-details">
              {evt.eventType === 'subscription_renewed' && (
                <span>{formatSGT(evt.previousBaseEndDate)} → {formatSGT(evt.newBaseEndDate)}</span>
              )}
              {evt.eventType === 'promo_extension_applied' && (
                <span>+{evt.extensionDays}d via {evt.promoCode} ({evt.previousPromoExtensionDays} → {evt.newPromoExtensionDays} days)</span>
              )}
              {evt.eventType === 'promo_days_forfeited' && (
                <span>Forfeited {evt.forfeitedDays}d — effective date reset to {formatSGT(evt.newEffectiveEndDate)}</span>
              )}
              {evt.eventType === 'manual_adjustment' && (
                <span>{evt.previousDays} → {evt.newDays} days — "{evt.reason}" (by {evt.adminId})</span>
              )}
              {evt.eventType === 'subscription_activated' && (
                <span>Activated with base end date {formatSGT(evt.baseEndDate)}</span>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

const ManualAdjustForm = ({ userId, currentDays, adminId, onSuccess }) => {
  const [newDays, setNewDays] = useState(currentDays ?? 0);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    const days = parseInt(newDays, 10);
    if (isNaN(days) || days < 0 || days > 730) {
      setError('Value must be between 0 and 730.');
      return;
    }
    if (!reason.trim()) {
      setError('A written reason is required.');
      return;
    }
    if (!API_ENDPOINTS.ADMIN.SUBSCRIPTION_END_DATE) {
      setError('Subscription end date service URL not configured. Deploy the Lambda and set VITE_ADMIN_SUBSCRIPTION_END_DATE_URL.');
      return;
    }

    try {
      setSubmitting(true);
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();

      const response = await fetch(API_ENDPOINTS.ADMIN.SUBSCRIPTION_END_DATE, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'manualAdjustPromoExtensionDays',
          userId,
          newDays: days,
          adminId,
          reason: reason.trim(),
        }),
      });

      const data = await response.json();
      if (!response.ok || data.error) {
        throw new Error(data.error || `Request failed (${response.status})`);
      }

      setSuccess(true);
      setReason('');
      onSuccess?.();
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="sed-panel__adjust-success">
        ✅ Promo days updated successfully.
        <button className="sed-panel__link-btn" onClick={() => setSuccess(false)}>Make another adjustment</button>
      </div>
    );
  }

  return (
    <form className="sed-panel__adjust-form" onSubmit={handleSubmit}>
      <div className="sed-panel__form-row">
        <label htmlFor="sed-new-days">New Promo Extension Days (0–730)</label>
        <input
          id="sed-new-days"
          type="number"
          min={0}
          max={730}
          value={newDays}
          onChange={(e) => setNewDays(e.target.value)}
          className="admin-input sed-panel__days-input"
          required
        />
      </div>
      <div className="sed-panel__form-row">
        <label htmlFor="sed-reason">Reason (required)</label>
        <textarea
          id="sed-reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Explain why this adjustment is being made..."
          rows={3}
          className="admin-input"
          required
        />
      </div>
      {error && <div className="sed-panel__form-error">⚠️ {error}</div>}
      <button
        type="submit"
        className="admin-btn admin-btn--primary"
        disabled={submitting}
      >
        {submitting ? 'Saving...' : 'Apply Adjustment'}
      </button>
    </form>
  );
};

// ─── Main Component ──────────────────────────────────────────────────────────

const SubscriptionEndDatePanel = ({ user, isSuperAdmin }) => {
  const [info, setInfo] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAdjustForm, setShowAdjustForm] = useState(false);

  const userId = user?.userId;
  const accountType = user?.attributes?.['custom:account_type'];
  const subscriptionStatus = user?.attributes?.['custom:subscription_status'];
  const adminId = user?.userId; // current admin's userId — passed from parent

  const fetchEndDateInfo = useCallback(async () => {
    if (!API_ENDPOINTS.ADMIN.SUBSCRIPTION_END_DATE) {
      setError('Subscription end date service not yet deployed. Set VITE_ADMIN_SUBSCRIPTION_END_DATE_URL to enable this panel.');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();

      // Fetch end date info
      const infoRes = await fetch(API_ENDPOINTS.ADMIN.SUBSCRIPTION_END_DATE, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'getSubscriptionEndDateInfo', userId }),
      });
      const infoData = await infoRes.json();
      if (infoRes.ok && !infoData.error) {
        setInfo(infoData.body ?? infoData);
      }

      // Fetch event log (via subscription Lambda which queries DynamoDB EVENT# records)
      const eventsRes = await fetch(API_ENDPOINTS.ADMIN.SUBSCRIPTION_END_DATE, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'getSubscriptionEvents', userId }),
      });
      const eventsData = await eventsRes.json();
      if (eventsRes.ok && !eventsData.error) {
        const rawEvents = eventsData.body?.events ?? eventsData.events ?? [];
        // Sort reverse chronological
        setEvents([...rawEvents].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)));
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (userId) fetchEndDateInfo();
  }, [userId, fetchEndDateInfo]);

  const isInactive = subscriptionStatus === 'inactive' || !subscriptionStatus;
  const isFamilyChild = accountType === 'child' || accountType === 'family_child';

  return (
    <div className="sed-panel">
      {/* End Date Info */}
      <div className="admin-card">
        <div className="admin-card-header">
          <h3 className="admin-card-title">📅 Subscription End Date</h3>
          {isSuperAdmin && !isFamilyChild && info && !info.isLifetime && (
            <button
              className="admin-btn admin-btn--secondary admin-btn--sm"
              onClick={() => setShowAdjustForm((v) => !v)}
            >
              {showAdjustForm ? 'Cancel' : '✏️ Adjust Promo Days'}
            </button>
          )}
        </div>

        {loading && <div className="sed-panel__loading">Loading end date info...</div>}

        {error && (
          <div className="admin-alert admin-alert--warning sed-panel__alert">
            ⚠️ {error}
          </div>
        )}

        {!loading && !error && (
          <div className="sed-panel__body">
            <FamilyContext user={user} info={info} />

            {isFamilyChild ? (
              <div className="sed-panel__child-note">
                This is a family child account. Subscription end date is governed by the parent account.
              </div>
            ) : isInactive && !info ? (
              <div className="sed-panel__inactive">No Active Subscription</div>
            ) : info ? (
              <EndDateDisplay info={info} />
            ) : (
              <div className="sed-panel__inactive">No Active Subscription</div>
            )}

            {showAdjustForm && isSuperAdmin && !isFamilyChild && (
              <div className="sed-panel__adjust-section">
                <h4>Manual Promo Days Adjustment</h4>
                <ManualAdjustForm
                  userId={userId}
                  currentDays={info?.promoExtensionDays ?? 0}
                  adminId={adminId}
                  onSuccess={() => {
                    setShowAdjustForm(false);
                    fetchEndDateInfo();
                  }}
                />
              </div>
            )}
          </div>
        )}
      </div>

      {/* Event Timeline */}
      <div className="admin-card">
        <div className="admin-card-header">
          <h3 className="admin-card-title">📋 Subscription Event History</h3>
        </div>
        {loading ? (
          <div className="sed-panel__loading">Loading events...</div>
        ) : (
          <div className="sed-panel__body">
            <EventTimeline events={events} />
          </div>
        )}
      </div>
    </div>
  );
};

export default SubscriptionEndDatePanel;
